"use client"
import { useAttributionFormatters } from "@/hooks/use-attribution-formatters"


import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

import { DEFAULT_SHOPIFY_PERIOD, type ShopifyPeriod } from "./period-constants"
import { ShopifyPeriodSelector } from "./shopify-period-selector"
import type { AttributionDateRange } from "./types"
import { useTranslations } from "next-intl"


interface StoreSelectProps {
  stores: { id: string; shop_domain: string }[]
  selectedStore: string
  onStoreChange: (storeId: string) => void
}

export function StoreSelect({ stores, selectedStore, onStoreChange }: StoreSelectProps) {
  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardReportScopeControls")

  return (
    <Select value={selectedStore} onValueChange={onStoreChange}>
      <SelectTrigger className="w-[220px]">
        <SelectValue placeholder={tUi("selectStore")} />
      </SelectTrigger>
      <SelectContent>
        {stores.map((s) => (
          <SelectItem key={s.id} value={s.id}>{s.shop_domain}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface PeriodFieldProps {
  period: ShopifyPeriod
  customRange: { from: string; to: string } | null
  onPeriodChange: (period: ShopifyPeriod, customRange?: { from: string; to: string }) => void
  dateRange: AttributionDateRange
}

export function PeriodField({ period, customRange, onPeriodChange, dateRange }: PeriodFieldProps) {
  const { formatDateRangeLabelInTimezone } = useAttributionFormatters()

  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardReportScopeControls")

  return (
    <>
      <label className="text-sm font-medium"><span key={tUi("period")}>{tUi("period")}</span></label>
      <div className={cn("relative rounded-md", period !== DEFAULT_SHOPIFY_PERIOD && "ring-2 ring-primary/30")}>
        <ShopifyPeriodSelector
          period={period}
          customRange={customRange}
          onPeriodChange={onPeriodChange}
        />
        <div className="absolute left-0 hidden text-[10px] leading-tight text-muted-foreground sm:block" style={{ top: "calc(100% + 3px)" }} data-testid="date-range-badge">
          <span>(</span>{formatDateRangeLabelInTimezone(
            dateRange.month_start_utc,
            new Date(dateRange.month_end_utc.getTime() - 86_400_000),
            dateRange.timezone,
          )}<span>)</span>
        </div>
      </div>
    </>
  )
}
